/**
 * Compact chart labels preserve numeric scale while fitting inside responsive
 * dashboard cards where full locale-formatted values would overlap or clip.
 */

const COMPACT_THRESHOLDS = [
  { minimum: 1_000_000_000, suffix: 'B' },
  { minimum: 1_000_000, suffix: 'M' },
  { minimum: 1_000, suffix: 'K' },
] as const;

const COMPACT_ROLLOVER = 1_000;

const getCompactThreshold = (absoluteValue: number) => {
  const thresholdIndex = COMPACT_THRESHOLDS.findIndex(
    ({ minimum }) => absoluteValue >= minimum,
  );
  const threshold = COMPACT_THRESHOLDS[thresholdIndex];

  if (!threshold) return undefined;

  const roundedScaledValue = Number(
    (absoluteValue / threshold.minimum).toFixed(1),
  );
  return roundedScaledValue >= COMPACT_ROLLOVER && thresholdIndex > 0
    ? COMPACT_THRESHOLDS[thresholdIndex - 1]
    : threshold;
};

const getCurrencySymbol = (currency: string): string => {
  const currencyPart = new Intl.NumberFormat('en-US', {
    currency,
    maximumFractionDigits: 0,
    style: 'currency',
  })
    .formatToParts(0)
    .find(({ type }) => type === 'currency');
  return currencyPart?.value ?? `${currency} `;
};

export const formatCompactNumber = (
  value: number,
  currency?: string,
): string => {
  const absoluteValue = Math.abs(value);
  const threshold = getCompactThreshold(absoluteValue);
  const magnitude = threshold
    ? `${(absoluteValue / threshold.minimum).toFixed(1).replace(/\.0$/, '')}${
        threshold.suffix
      }`
    : absoluteValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const sign = value < 0 ? '-' : '';
  const currencySymbol = currency ? getCurrencySymbol(currency) : '';

  return `${sign}${currencySymbol}${magnitude}`;
};
